import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BsDatepickerConfig, BsDatepickerDirective, BsDatepickerInputDirective } from 'ngx-bootstrap/datepicker';
import { NgxMaskDirective } from 'ngx-mask';
import { ButtonComponent } from '@appShared/components/button/button.component';
import { IShareholder } from '@appShared/interfaces/[Model-based]/shareholder.interface';
import { ShareholderNewComponent } from './shareholder-new/shareholder-new.component';
import { ShareholderPlanListComponent } from './shareholder-plan/shareholder-plan-list/shareholder-plan-list.component';
import { ShareholderDividendComponent } from './shareholder-dividend/shareholder-dividend.component';
import {
  ShareholderEditSectionData,
  ShareholderEditSection_,
  AdminService
} from '@appShared/services/admin.service';
import { ShareholderService } from '@appShared/services/shareholder.service';
import { CommonService } from '@appShared/services/common.service';
import * as _ from 'lodash';

@Component({
  selector: 'app-shareholder-edit',
  templateUrl: './shareholder-edit.component.html',
   styleUrls: ['./shareholder-edit.component.less'],
   imports: [
      ReactiveFormsModule,
      BsDatepickerDirective,
      BsDatepickerInputDirective,
      NgxMaskDirective,
      ButtonComponent,
      ShareholderNewComponent,
      ShareholderPlanListComponent,
      ShareholderDividendComponent
   ]
})
export class ShareholderEditComponent implements OnInit, OnChanges {
  @Input() shareholder: IShareholder;
  @Input() canEdit: boolean;
  @Output() shareholderUpdated = new EventEmitter();
  ShareholderEditSection_ = ShareholderEditSection_;

  /* form/form-fields */

  shareholderForm: FormGroup;
  pin: FormControl;
  datePickerBaseConfig: Partial<BsDatepickerConfig> = {};
  repurchaseOptionAsOfDatePicker: FormControl;
  mouseoverSaveShareholder: boolean;
  isSubmitting: boolean;

  constructor(
    private _adminService: AdminService,
    private _commonService: CommonService,
    private _shareholderService: ShareholderService
  ) {
    this.datePickerBaseConfig = this._commonService.datePickerBaseConfig;
  }

  ngOnInit(): void {
    this._createFormGroup();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes?.['shareholder']?.isFirstChange()) {
      this._setShareholderData();
    }
  }

  /*
   * private methods
   * */

  private _createFormGroup(): void {
    this.pin = new FormControl(null, [
      Validators.minLength(4),
      Validators.maxLength(4),
      Validators.required
    ]);
    this.repurchaseOptionAsOfDatePicker = new FormControl(null);

    this.shareholderForm = new FormGroup({
      pin: this.pin,
      repurchaseOptionAsOfDatePicker: this.repurchaseOptionAsOfDatePicker
    });
  }

  private _setShareholderData(): void {
    const shareholder = this.shareholder;
    this.shareholderForm.patchValue({
      pin: shareholder.pin,
      repurchaseOptionAsOfDatePicker: shareholder.repurchaseOptionAsOf
        ? new Date(shareholder.repurchaseOptionAsOf)
        : null
    });

    this.canEdit ? this.shareholderForm.enable() : this.shareholderForm.disable();
  }

  private _emitShareholderUpdated(shareholderEditSectionData: ShareholderEditSectionData): void {
    if (shareholderEditSectionData.shareholder) {
      this.shareholder = shareholderEditSectionData.shareholder;
    }
    this.shareholderUpdated.emit(shareholderEditSectionData);
  }

  /*
   * public methods
   * */

  saveShareholder(): void {
    this.isSubmitting = true;

    const dateService = this._commonService.dateTime;
    const shareholderFormValues = this.shareholderForm.value;
    const pin = shareholderFormValues.pin;
    const repurchaseOptionAsOf = dateService.formatDate(
      shareholderFormValues.repurchaseOptionAsOfDatePicker
    );
    const shareholder = {
      ...this.shareholder,
      pin, repurchaseOptionAsOf
    } as IShareholder;

    this._adminService
      .createUpdateShareholder(shareholder)
      .then(shrldr => {
        this._commonService.messageUser('Shareholder updated!');
        this._emitShareholderUpdated({
          shareholderEditSection: ShareholderEditSection_.basicInfo,
          shareholder: shrldr,
          refresh: true
        } as ShareholderEditSectionData);
      })
      .catch(() => {
        /* Do Nothing */
      })
      .finally(() => setTimeout(() => (this.isSubmitting = false), 500));
  }

  shareholderSectionUpdated(
    shareholderEditSectionData: ShareholderEditSectionData
  ): void {
    const incomingShareholder = _.cloneDeep(
      shareholderEditSectionData.shareholder
    );

    if (shareholderEditSectionData.refresh && incomingShareholder.id) {
      this._adminService
        .getShareholder(incomingShareholder.id)
        .then(shareholder => {

          this._emitShareholderUpdated({
            ...shareholderEditSectionData,
            shareholder
          } as ShareholderEditSectionData);
        })
        .catch(() => {
          /* Do Nothing */
        });
    } else {
      this._emitShareholderUpdated(shareholderEditSectionData);
    }
  }

  viewReport(): void {
    this._shareholderService.generatePlanPdf(this.shareholder);
  }
}
